import * as z from "zod";
import { type Actor, type ActorDelegation, cursorPagination, delegatedAccessKinds, uuidV7 } from "../index";

// Delegated access (ADR-262, C-47): a user delegates part of its permissions to a client, a personal token made in
// Backstage or the connection of an assistant (Claude) made by an OAuth consent (P5.15b). One table and one check in
// identity; the roles say who may delegate what (party). What the gateways and the apps share: the format of a token,
// the permissions no access ever carries, the inputs and the answers.

// --- The token ---

/**
 * The prefixes of the bearer tokens of Nexxt (ADR-262 point 3): a token found where it should not be is recognised by
 * them, by the rule of gitleaks of the repo and by the secret scanners. `oat` and `ort` are the access and refresh
 * tokens of the OAuth connections, `oac` their codes of authorization (P5.15b, ADR-263).
 */
export const accessTokenPrefixes = {
  personal: "nxt_pat_",
  oauthAccess: "nxt_oat_",
  oauthRefresh: "nxt_ort_",
  oauthCode: "nxt_oac_",
} as const;
export type AccessTokenPrefix = (typeof accessTokenPrefixes)[keyof typeof accessTokenPrefixes];

const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
/** 256 random bits in base62: 43 characters. */
export const ACCESS_TOKEN_SECRET_LENGTH = 43;
/** CRC32 of prefix and secret, in base62. */
const CHECKSUM_LENGTH = 6;

/** Any token of Nexxt, as the rule of gitleaks finds it: the prefix, the secret and the checksum. */
export const accessTokenPattern = /^nxt_(pat|oat|ort|oac)_[0-9A-Za-z]{49}$/;

const CRC_TABLE = Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});

function crc32(text: string): number {
  let crc = 0xffffffff;
  for (const byte of new TextEncoder().encode(text)) crc = (CRC_TABLE[(crc ^ byte) & 0xff] ?? 0) ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** A number in base62 on `length` characters, zeros on the left. */
export function base62(value: number | bigint, length: number): string {
  let rest = BigInt(value);
  let out = "";
  for (let i = 0; i < length; i++) {
    out = BASE62[Number(rest % 62n)] + out;
    rest /= 62n;
  }
  return out;
}

/** The checksum a token ends with: it tells a typo or a truncated copy from a token, without the DB. */
export const accessTokenChecksum = (prefixAndSecret: string): string => base62(crc32(prefixAndSecret), CHECKSUM_LENGTH);

/**
 * Whether the text has the form of a token of that kind, checksum included: a gateway refuses anything else before
 * asking identity, and identity before hashing.
 */
export function isWellFormedAccessToken(token: string, prefix: AccessTokenPrefix): boolean {
  if (!accessTokenPattern.test(token) || !token.startsWith(prefix)) return false;
  const body = token.slice(0, -CHECKSUM_LENGTH);
  return accessTokenChecksum(body) === token.slice(-CHECKSUM_LENGTH);
}

/** What a list shows of a token after it was made: its prefix and its last four characters, never more. */
export const accessTokenHint = (token: string): string => `${token.slice(0, 8)}…${token.slice(-4)}`;

// --- The permissions ---

/**
 * The permissions no delegated access carries, whatever the administration says (ADR-262 point 5): who administers the
 * access of others, the configuration, a decision on credit or payments, the bodies of the messages to and from TIL.
 * The gates of approval stay human too, in `decideGate`, and so do the approval, the pause and the resumption of a
 * send of Guestlist (`crm.send`, ADR-287 §4, ADR-319).
 */
export const neverDelegablePermissions = [
  "user.manage",
  "role.manage",
  "delegated_access.manage",
  "config.manage",
  "credit.manage",
  "payment.manage",
  "integration.payload.read",
  "crm.send",
  "contact.erase",
] as const;

export const isNeverDelegable = (permission: string): boolean =>
  (neverDelegablePermissions as readonly string[]).includes(permission);

/**
 * A permission that only reads, by its key (`linesheet.read`, `stock.read`): what the "read only" choice of an area
 * gives, and what a role delegates when a migration first sets its policy.
 */
export const isReadPermission = (permission: string): boolean => permission.endsWith(".read");

/** The longest life of a personal token, whatever the policy of a role says (ADR-262 point 4). */
export const MAX_TOKEN_DAYS = 365;
/** The life a new token is offered with. */
export const DEFAULT_TOKEN_DAYS = 30;
/** Live personal tokens of one user: one more is refused until one is revoked or expires. */
export const MAX_ACTIVE_TOKENS = 20;
/** Days before the end of a token when its holder gets the email. */
export const TOKEN_EXPIRY_NOTICE_DAYS = 7;
/** The page of Backstage with the tokens of the signed-in user: the route of the app and the link of the emails. */
export const myTokensPath = "/settings/tokens";

/** A permission key as the catalogue of party has it. */
const permissionKey = z.string().trim().min(1).max(100);

// --- What a user does with its tokens ---

/** `active` until it is revoked or reaches its end; a revoked one stays revoked even after its end. */
export const delegatedAccessStatuses = ["active", "expired", "revoked"] as const;
export type DelegatedAccessStatus = (typeof delegatedAccessStatuses)[number];

const person = z.object({ id: z.uuid(), name: z.string().nullable(), email: z.string() });

/** A delegated access as its holder and the administrator see it: never the token, never its hash. */
export const delegatedAccessDto = z
  .object({
    id: z.uuid(),
    kind: z.enum(delegatedAccessKinds),
    name: z.string(),
    note: z.string().nullable(),
    holder: person,
    /** The permissions chosen when it was made, sorted: at each call they count only while the holder's roles allow. */
    permissions: z.array(z.string()),
    /** `nxt_pat_…a1B2`; null for a connection, whose tokens are never shown. */
    tokenHint: z.string().nullable(),
    /** The client of a connection (`Claude`); null for a token. */
    clientName: z.string().nullable(),
    /** The `client_id` of a connection: the URL of its metadata document, or the id a registered client got. */
    clientId: z.string().nullable(),
    /** The scopes the client of a connection asked for (`mcp:read`, `mcp:write`); null for a token. */
    scopes: z.array(z.string()).nullable(),
    status: z.enum(delegatedAccessStatuses),
    createdAt: z.iso.datetime(),
    expiresAt: z.iso.datetime().nullable(),
    revokedAt: z.iso.datetime().nullable(),
    revokedBy: person.omit({ email: true }).nullable(),
    lastUsedAt: z.iso.datetime().nullable(),
    lastUsedIp: z.string().nullable(),
    /** The `User-Agent` of the last call, cut to 200 characters. */
    lastUsedClient: z.string().nullable(),
  })
  .meta({ id: "DelegatedAccess" });
export type DelegatedAccessDto = z.output<typeof delegatedAccessDto>;

export const delegatedAccessListDto = z.object({ items: z.array(delegatedAccessDto) });

/** What the form of a new token offers the signed-in user, from its roles as they are now. */
export const delegationOptionsDto = z.object({
  tokens: z.object({
    /** A role of the user lets it make personal tokens. */
    allowed: z.boolean(),
    /** The longest life its roles allow, at most `MAX_TOKEN_DAYS`; null when not allowed. */
    maxDays: z.number().int().nullable(),
    defaultDays: z.number().int(),
    active: z.number().int(),
    maxActive: z.number().int(),
  }),
  /** The permissions the user may delegate now: held through a role that delegates them. */
  permissions: z.array(z.object({ key: z.string(), module: z.string(), description: z.string().nullable() })),
  /** The access via MCP (ADR-263): on for everyone (`mcp.enabled`), and not turned off for this user. */
  mcp: z.object({
    enabled: z.boolean(),
    blocked: z.boolean(),
    /** The address to give Claude, `https://…/mcp`; null where the environment has no MCP server. */
    url: z.string().nullable(),
  }),
  /** The connections of an assistant: whether a role lets the user make them, and how many it has live. */
  connections: z.object({
    allowed: z.boolean(),
    active: z.number().int(),
    maxActive: z.number().int(),
    /** What a connection may carry, by area: what a role delegates to connections. */
    permissions: z.array(z.object({ key: z.string(), module: z.string(), description: z.string().nullable() })),
  }),
});
export type DelegationOptionsDto = z.output<typeof delegationOptionsDto>;

export const getDelegationOptionsInput = z.object({});
export type GetDelegationOptionsInput = z.input<typeof getDelegationOptionsInput>;

export const listMyDelegatedAccessInput = z.object({});
export type ListMyDelegatedAccessInput = z.input<typeof listMyDelegatedAccessInput>;

/** A new personal token; the id comes from the client (ADR-046), so a retry never makes a second one. */
export const createPersonalTokenInput = z.object({
  id: uuidV7,
  name: z.string().trim().min(1).max(100),
  note: z.string().trim().max(500).nullable().optional(),
  /** Its life from now, in days: never without an end. */
  expiresInDays: z.number().int().min(1).max(MAX_TOKEN_DAYS),
  permissions: z.array(permissionKey).min(1).max(300),
});
export type CreatePersonalTokenInput = z.input<typeof createPersonalTokenInput>;

/** The token, shown this once: Nexxt keeps only its keyed hash. */
export const createdPersonalTokenDto = z.object({ access: delegatedAccessDto, token: z.string() });

/** The holder revokes its own; an administrator with `delegated_access.manage` any. */
export const revokeDelegatedAccessBody = z.object({ reason: z.string().trim().min(1).max(500).optional() });
export const revokeDelegatedAccessInput = revokeDelegatedAccessBody.extend({ accessId: z.uuid() });
export type RevokeDelegatedAccessInput = z.input<typeof revokeDelegatedAccessInput>;
export const delegatedAccessParams = revokeDelegatedAccessInput.pick({ accessId: true });

/** Every delegated access, for the administrator: newest first, by cursor. */
export const listDelegatedAccessInput = z.object({
  status: z.enum(delegatedAccessStatuses).optional(),
  kind: z.enum(delegatedAccessKinds).optional(),
  userId: z.uuid().optional(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 200 }),
});
export type ListDelegatedAccessInput = z.input<typeof listDelegatedAccessInput>;

// --- The check of a call (gateway-mcp, P5.15b) ---

/** What a gateway passes of a call with a bearer token: never logged, never kept. */
export const verifyDelegatedAccessInput = z.object({
  token: z.string().max(100),
  /** `CF-Connecting-IP` of the call. */
  ip: z.union([z.ipv4(), z.ipv6()]).nullable(),
  /** `User-Agent` of the call, cut by identity. */
  client: z.string().max(1000).nullable(),
  /**
   * The resource the gateway serves (RFC 8707): an access token of a connection is accepted only by the resource it was
   * given for. A personal token has no audience.
   */
  resource: z.string().max(500).optional(),
});
export type VerifyDelegatedAccessInput = z.input<typeof verifyDelegatedAccessInput>;

/** The Actor of a call made with a delegated access: the holder's, with the permissions narrowed and the access named. */
export type DelegatedActor = Actor & { userId: string; delegation: ActorDelegation };

// --- Errors ---

/** Codes of the delegated access (ADR-046), each with a text in Backstage. */
export type DelegatedAccessErrorCode =
  /** No role of the user lets it make one, the user is external (D-28), or the call comes through a delegated access. */
  | "delegation_not_allowed"
  /** A permission the user may not delegate, or a life longer than its roles allow: `details.issues`. */
  | "delegated_access_invalid"
  | "access_token_limit_reached"
  /** The environment has no key for the hashes of the tokens (`ACCESS_TOKEN_PEPPER`). */
  | "access_tokens_unavailable"
  /** The token of a call is unknown, revoked, expired, or its holder cannot use it any more: always the same. */
  | "access_token_invalid"
  /** The access via MCP is off for everyone (`mcp.enabled`) or for the user (ADR-263). */
  | "mcp_disabled";

/** Rules of the `details.issues` of `delegated_access_invalid`. */
export type DelegatedAccessIssueRule = "not_delegable" | "too_long";
